import { isChordLine, isLyricsLine } from './chordPatterns';
import { isInstrumentalPart } from './parts';

export { isChordLine, isLyricsLine };
const isTextLine = (line) => line.trim().length > 0;

// "{8x,15}" or "{8x,15.3}" on its own line at the top of the lyrics: a stopwatch-measured pass
// length written by RepeatTimerModal. Read both by ViewSongPart (to drive the tap counter) and by
// songTiming (as the one exact duration a part can carry), so it lives here rather than in either.
export const REPEAT_REGEX = /^\{(\d+)x,(\d+(?:\.\d+)?)\}(\r\n|\r|\n)?/;

export const parseRepeatDirective = (lyrics) => {
  const match = lyrics.match(REPEAT_REGEX);

  if (!match) return { count: null, duration: null, strippedLyrics: lyrics };

  return {
    count: parseInt(match[1], 10),
    duration: parseFloat(match[2]),
    strippedLyrics: lyrics.slice(match[0].length),
  };
};

// Beyond this many slashes the run is too long to read, so we collapse to a
// single bar plus an "xN" count at the end (e.g. "/ Line ... Line / x6").
const MAX_SLASHES = 4;

// Lyrics render in a proportional font where a slash is wider than a space, so matching the
// mark's width in spaces alone still leaves an indented chord line short of the one above it.
// Two extra spaces make up the difference.
const CHORD_INDENT_PADDING = 1;

/* ---------------------------------------------------------------------------
 * Hand-typed repeat marks
 *
 * applyRepeatSlashes below draws the marks at render time from repeatCount, into a copy — it never
 * writes them back. So a slash sitting in a part's *stored* lyrics was typed by hand, and
 * repeatCount is still 1: the chart says "play this twice" while the data says "play it once".
 *
 * Smart auto scroll times a part from repeatCount, so left alone it would give that part a single
 * pass worth of time and race through it. The marks are the same notation applyRepeatSlashes
 * writes, though, so rather than mis-time the song — or refuse to scroll it — we read the count
 * back out of them: countManualRepeats below, called from songTiming's getRepeatCount.
 * ------------------------------------------------------------------------- */

// Matched against a trimmed line. Deliberately mirrors what applyRepeatSlashes writes: an opening
// run on the first marked line, a closing run (or the compact "/ xN") on the last. Each captures
// the number it carries, since the marks are read for their count and not only spotted. A run may
// sit against a word ("/Amazing", "sound/"), as typed by hand. No lookbehind (Safari < 16.4 can't
// parse it): the closing ones match the character before the run, and group 1 is the mark itself.
const OPENING_MARK = /^(\/{1,6})(?!\/)/;
const CLOSING_MARK = /(?:^|[^/])(\/{1,6})$/;
const CLOSING_COUNT_MARK = /(?:^|[^/])(\/\s*x\s*(\d+))$/i;

// A slash standing alone between chords is a beat ("C / / /"), not a repeat
const BEAT_SLASH = /(?:^|\s)\/+(?=\s|$)/;

// The lines a repeat is marked on: the words, or on a part with none, its chords — where
// applyRepeatSlashes puts them on an instrumental, and where they're typed on any chords-only part
const markedLineTest = (lines) => (lines.some(isLyricsLine) ? isLyricsLine : isTextLine);

const hasClosingMark = (line) => CLOSING_COUNT_MARK.test(line) || CLOSING_MARK.test(line);

// The last line ending in a closing mark, so a label or note after the repeat ("Figura") doesn't
// hide it. -1 when none does.
const findClosingIndex = (lines) => {
  for (let i = lines.length - 1; i >= 0; i--) {
    if (hasClosingMark(lines[i].trimEnd())) return i;
  }
  return -1;
};

/**
 * How many passes a part's hand-typed marks say it gets — 1 when it carries none.
 *
 * Both ends are required — the opening on the first marked line, the closing on the last marked line
 * that has one — and the part has to contain a letter, because a slash on its own is also
 * ordinary chart notation — a bar of rhythm slashes ("C / / /") opens without closing, and a line
 * of nothing but slashes carries no words. Demanding the full pair keeps those out, and on a part
 * marked on its chords, so does a beat slash anywhere between the two.
 *
 * The count is the run length plus one (one slash means play it twice), matching what
 * applyRepeatSlashes draws, or the number itself in the compact "/ xN" form. Where the two ends
 * disagree — hand-typed marks need not be symmetrical — the longer run wins.
 */
export const countManualRepeats = (part) => {
  const { strippedLyrics } = parseRepeatDirective(part?.lyrics || '');
  if (!strippedLyrics || !/[a-z]/i.test(strippedLyrics)) return 1;

  const allLines = strippedLyrics.split(/\r\n|\r|\n/);
  const isMarked = markedLineTest(allLines);
  const lines = allLines.filter(isMarked).map((line) => line.trim());
  if (lines.length === 0) return 1;

  const opening = lines[0].match(OPENING_MARK);
  if (!opening) return 1;

  const closingIdx = findClosingIndex(lines);
  if (closingIdx === -1) return 1;

  const last = lines[closingIdx];
  const counted = last.match(CLOSING_COUNT_MARK);
  const closing = counted || last.match(CLOSING_MARK);
  if (!closing) return 1;

  // On chords, a beat between the marks makes it a rhythm chart whose slashes happen to reach both ends
  if (isMarked !== isLyricsLine) {
    const marked = lines.slice(0, closingIdx + 1).join('\n');
    if (BEAT_SLASH.test(marked.slice(opening[0].length, marked.length - closing[1].length))) return 1;
  }

  // "/ x6" states the number outright. Anything under two isn't a repeat, whatever it says.
  if (counted) return Math.max(1, parseInt(counted[2], 10));

  return Math.max(opening[1].length, closing[1].length) + 1;
};

/** True when a part's repeats were typed rather than counted. */
export const hasManualRepeatSlashes = (part) => countManualRepeats(part) > 1;

/**
 * The lyrics with their hand-typed repeat marks taken off — only marks countManualRepeats would
 * read, so rhythm slashes stay. The opening run becomes spaces, keeping any chords above in line.
 */
export const stripManualRepeatSlashes = (lyrics) => {
  if (!lyrics || countManualRepeats({ lyrics }) < 2) return lyrics;

  const lines = lyrics.split(/\r\n|\r|\n/);
  const isMarked = markedLineTest(lines);
  const firstIdx = lines.findIndex(isMarked);
  const lastIdx = findClosingIndex(lines.map((line) => (isMarked(line) ? line : '')));

  lines[firstIdx] = lines[firstIdx].replace(
    /^(\s*)(\/{1,6})(?!\/)/,
    (matched, space, run) => space + ' '.repeat(run.length),
  );

  const last = lines[lastIdx].trimEnd();
  const closing = CLOSING_COUNT_MARK.test(last) ? CLOSING_COUNT_MARK : CLOSING_MARK;
  lines[lastIdx] = last.replace(closing, (matched, mark) => matched.slice(0, matched.length - mark.length));

  return lines.join('\n');
};

// Wraps a part's lyrics in repeat marks based on repeatCount (>= 2).
// Up to MAX_SLASHES slashes: repeatCount 2 -> "/", 3 -> "//", ... (repeatCount - 1 slashes),
// prefixing the first lyrics line and suffixing the last.
// Above MAX_SLASHES: a single "/" prefix and a "/ xN" suffix instead of a long run.
// Marks attach only to lyrics lines. Prefixing shifts the first lyrics line right,
// so the chord line directly above it (if any) is padded by the same width to keep alignment.
// Instrumental parts (Intro/Music/Instrumental/Outro) are the exception: they're often a line
// of chords and nothing else, so with no lyrics line to mark the chords take the marks instead.
export const applyRepeatSlashes = (lyrics, repeatCount, partName) => {
  if (!repeatCount || repeatCount < 2 || !lyrics) return lyrics;

  const slashCount = repeatCount - 1;
  const compact = slashCount > MAX_SLASHES;
  const startMark = compact ? '/' : '/'.repeat(slashCount);
  const endMark = compact ? `/ x${repeatCount}` : '/'.repeat(slashCount);
  const prefix = `${startMark} `;
  const lines = lyrics.split(/\r\n|\r|\n/);

  // Chords stand in for the lyrics only on instrumental parts — every other part leaves its
  // chord lines untouched, so a chords-only Verse still gets no marks.
  const marksChords = !lines.some(isLyricsLine) && isInstrumentalPart(partName);
  const isMarkable = marksChords ? isTextLine : isLyricsLine;

  const firstIdx = lines.findIndex(isMarkable);
  if (firstIdx === -1) return lyrics; // nothing to mark (e.g. chords-only on a lyrics part)

  let lastIdx = firstIdx;
  for (let i = lines.length - 1; i > firstIdx; i--) {
    if (isMarkable(lines[i])) {
      lastIdx = i;
      break;
    }
  }

  // Prepend first so the single-line case (firstIdx === lastIdx) still gets the suffix
  lines[firstIdx] = prefix + lines[firstIdx];
  if (marksChords) {
    // The mark shifts the first chord line right, so the rest of the block is indented to
    // match and the chords stay lined up with each other
    const indent = ' '.repeat(prefix.length + CHORD_INDENT_PADDING);

    for (let i = firstIdx + 1; i <= lastIdx; i++) {
      if (isTextLine(lines[i])) lines[i] = indent + lines[i];
    }
  } else if (firstIdx > 0 && isChordLine(lines[firstIdx - 1])) {
    // A lyrics mark would pull the line out of alignment with the chords above it
    lines[firstIdx - 1] = ' '.repeat(prefix.length) + lines[firstIdx - 1];
  }
  lines[lastIdx] = `${lines[lastIdx]} ${endMark}`;

  return lines.join('\n');
};
